<template>
  <section class="content-container" :style="styles.container">
    <div
      :id="`input__${section}`"
    >
      <div v-if="!content && !paramOptions" class="empty-state">(no content)</div>
      <textarea
        ref="textarea"
        type="text"
        :class="isEmpty ? 'empty-state' : ''"
        @keyup="onInputChange"
        @blur="onInputBlur"
        @focus="onInputFocus"
        @click="onInputChange"
      >{{ content }}</textarea>
      <div
        v-if="showParamMenu"
        class="menu__param-options"
        :style="styles.paramMenuStyles(this.paramCoords)"
      >
        <ul>
          <li
            v-for="option in filteredParamOptions()"
            @click="() => onParamClick(option)"
          >
            {{ option }}
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>

<script>
function getOffset( el ) {
    var _x = 0;
    var _y = 0;
    while( el && !isNaN( el.offsetLeft ) && !isNaN( el.offsetTop ) ) {
        _x += el.offsetLeft - el.scrollLeft;
        _y += el.offsetTop - el.scrollTop;
        el = el.offsetParent;
    }
    return { top: _y, left: _x };
}
/**
 * returns x, y coordinates for absolute positioning of a span within a given text input
 * at a given selection point
 * @param {object} input - the input element to obtain coordinates for
 * @param {number} selectionPoint - the selection point for the input
 */
const getCursorXY = (input, selectionPoint) => {
  const {
    offsetLeft: inputX,
    offsetTop: inputY,
  } = input
  // create a dummy element that will be a clone of our input
  const div = document.createElement('div')
  // get the computed style of the input and clone it onto the dummy element
  const copyStyle = getComputedStyle(input)
  for (const prop of copyStyle) {
    div.style[prop] = copyStyle[prop]
  }
  // we need a character that will replace whitespace when filling our dummy element if it's a single line <input/>
  const swap = '.'
  const inputValue = input.tagName === 'INPUT' ? input.value.replace(/ /g, swap) : input.value
  // set the div content to that of the textarea up until selection
  const textContent = inputValue.substr(0, selectionPoint)
  // set the text content of the dummy element div
  div.textContent = textContent
  if (input.tagName === 'TEXTAREA') div.style.height = 'auto'
  // if a single line input then the div needs to be single line and not break out like a text area
  if (input.tagName === 'INPUT') div.style.width = 'auto'
  // create a marker element to obtain caret position
  div.style.width = input.offsetWidth;
  div.style.height = input.offsetHeight;
  div.style.wordWrap = 'wrap'
  const span = document.createElement('span')
  // give the span the textContent of remaining content so that the recreated dummy element is as close as possible
  span.textContent = inputValue.substr(selectionPoint) || '.'
  // append the span marker to the div
  div.appendChild(span)
  // append the dummy element to the body
  document.body.appendChild(div)
  // get the marker position, this is the caret position top and left relative to the input
  const { offsetLeft: spanX, offsetTop: spanY } = span
  // lastly, remove that dummy element
  // NOTE:: can comment this out for debugging purposes if you want to see where that span is rendered
  document.body.removeChild(div)
  // return an object with the x and y of the caret. account for input positioning so that you don't need to wrap the input
  return {
    x: inputX + spanX,
    y: inputY + spanY,
  }
}

import store from '../../store';
export default {
  name: 'MixedContentInput',
  props: {
    activeSetting: String,
    template: String,
    section: String,
    paramOptions: Array,
    inputStyle: Object,
    edit: Function,
    isEditing: Boolean
  },
  data() {
    return {
      addingNewParam: false,
      newContent: null,
      showParamOptions: false,
      showAddParamButton: false,
      isEmpty: false,
      cursorPosition: null,
      selectedParam: null,
      showParamMenu: false,
      paramCoords: null,
      styles: {
        container: {
          marginBottom: '1rem',
          border: '1px solid #E2E2E2',
          background: '#FFF',
          borderRadius: '3px',
          padding: '1rem',
          width: '100%'
        },
        paramMenuStyles: (coords) => ({
          background: '#fff',
          zIndex: 9999,
          position: 'absolute',
          top: coords.y ? `${coords.y + 30}px` : false,
          left: coords.x ? `${coords.x - 10}px` : false,
          overflowY: 'scroll',
          overflowX: 'hidden'
        }),
      }
    };
  },
  computed: {
    getActiveLocalRecord() {
      return store.getters.getActiveLocalRecord;
    },
    content() {
      if(this.section === 'backgroundUrl') {
        return this.getActiveLocalRecord[this.section];
      }
      if (this.section === 'title' || this.section === 'callToAction') {
        if (this.getActiveLocalRecord[this.section]) {
          return (
            this.getActiveLocalRecord[this.section][this.activeSetting] ||
            '(no content)'
          );
        } else {
          return '(no content)';
        }
      } else if (this.section === 'subject' || this.section === 'bodyText') {
        if (this.getActiveLocalRecord.message) {
          return this.getActiveLocalRecord.message[this.section] || '(no content)';
        } else {
          return '(no content)';
        }
      } else if (this.section === 'reason') {
        if (
          typeof this.getActiveLocalRecord.reason === 'string' &&
          this.getActiveLocalRecord.reason !== '\n'
        ) {
          return this.getActiveLocalRecord.reason;
        }

        return '(no content)';
      } else {
        return this.getActiveLocalRecord.help[this.section] || '(no content)';
      }
    },
  },
  methods: {
    onEmptyContent() {
      const ref = this.$refs.textarea;
      if (ref && ref.value) {
        return 'empty-state';
      }
      return '';
    },
    textareaResize() {
      this.$refs.textarea.style.minHeight = this.$refs.textarea.scrollHeight + 'px';
    },
    spanify(txt) {
      return txt.replace(/\b(\w+)\b/g, '<span>$1</span>');
    },
    onParamClick(param) {
      const val = this.$refs.textarea.value;
      const sel = this.$refs.textarea.selectionStart;
      const word = this.getWord(val, sel);
      if (word && this.isParam(word)) {
        // existing param, no }
        const i = val.indexOf(word);
        const first = val.substr(0, i);
        const last = val.substr(i + word.length, val.length)
        let str = `${first ? first : ''} {${param}} ${last ? last : ''}`;
        str = str.trim().replace(/\s+/g, ' ')
        this.$refs.textarea.value = str;
      } else if (word && !this.isParam(word) && this.isUnfinishedParam(word)){
        // new param, needs }
        const i = val.indexOf(word);
        const first = val.substr(0, i);
        const last = val.substr(i + word.length, val.length)
        let str = `${first ? first : ''} {${param}} ${last ? last : ''}`;
        str = str.trim().replace(/\s+/g, ' ')
        this.$refs.textarea.value = str;
      }
    },
    onInputBlur(e) {
      if (e.target.value === '' || e.target.value === ' ') {
        e.target.value = '(no content)';
        this.isEmpty = true;
      }
      const val = this.isEmpty ? '' : e.target.value;
      setTimeout(() => {
        this.showParamMenu = false;
        this.paramCoords = null;
      }, 200) // allow for param click to engage

      // handle update vuex
      const activeRecord = { ...this.getActiveLocalRecord };
      if (this.section === 'title' || this.section === 'callToAction') {
        if(!activeRecord[this.section]) {
            activeRecord[this.section] = {};
        }
        activeRecord[this.section][this.activeSetting] = val;
      } else if (this.section === 'subject' || this.section === 'bodyText') {
        if (activeRecord.message) {
          activeRecord.message[this.section] = val;
        } else {
          activeRecord.message = {};
          activeRecord.message[this.section] = val;
        }
      } else {
        activeRecord[this.section] = val;
      }
      store.commit('setActiveLocalRecord', activeRecord);
    },
    onInputFocus(e) {
      if (e.target.value === '(no content)') {
        e.target.value = '';
      }
    },
    onInputChange(e) {
      if (e.target.value === '(no content)') {
        this.isEmpty = true;
      } else {
        this.isEmpty = false;
      }
      this.textareaResize();
      const sel = this.$refs.textarea.selectionStart;
      const word = this.getWord(e.target.value, sel)
      const isParam = this.isUnfinishedParam(word);
      const xy = getCursorXY(e.target, sel);

      // if y is close to bottom of window,
      // invert popup

      if (xy.y > window.innerHeight - 100) xy.y = xy.y - 240;

      if (isParam) {
        this.paramCoords = xy;
        this.showParamMenu = true;
      } else {
        this.showParamMenu = false;
        this.paramCoords = null;
      }
    },
    isParam(word) {
      const first = word.indexOf('{');
      const last = word.indexOf('}');
      return (first === 0 && last === word.length - 1) ? true : false;
    },
    isUnfinishedParam(word) {
      const first = word.indexOf('{');
      return (first === 0) ? true : false;
    },
    getWord(val, offset) {
      const words = val.split(' ');
      let found;
      let count = 0;
      for (const i in words) {
        count += words[i].length
        if (i !== 0) count += 1;
        if (offset === count - 1) {
          // at beginning of word
          return words[i]
        } else if (offset === count) {
          // at end of word
          const ii = parseInt(i, 10) + 1;
          return words[ii]
        } else if (count > offset) {
          return words[i];
        }
      }
    },
    filteredParamOptions() {
      const sel = this.$refs.textarea.selectionStart;
      let word = this.getWord(this.$refs.textarea.value, sel);
      word = word.substring(1, word.length - 1)
      const arr = this.paramOptions.filter((o) => (o.indexOf(word) !== -1));
      console.log(arr);
      if (!arr.length) return this.paramOptions;
      return arr;
    },
  },
  mounted() {
    this.$refs.textarea.style.minHeight = this.$refs.textarea.scrollHeight + 'px';
    if (this.$refs.textarea.value === '(no content)') this.isEmpty = true;
  }
};
</script>
<style lang="scss" scoped>
.button__action {
  margin-top: 10px;
}

.input__title {
  color: #444;
  text-align: center;
}
.bold {
  font-family: 'proxima_nova_ltsemibold';
}

section.content-container textarea {
  width: 100%;
  font-size: 1.1em;
  resize: none;
  border: 0;
}

section.content-container .empty-state {
  color: #aaa;
}

#input__subject,
#input__body {
  font-size: 1rem !important;
}

.menu__param-options {
  box-shadow: 0 2px 5px 1px rgba(0,0,0,0.2);
  width: 250px;
  height: 200px;

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-flow: column nowrap;
    align-items: center;
    justify-content: flex-start;

    li {
      padding: 15px;
      border-bottom: 1px solid #ddd;
      margin: 0;
      width: 100%;
      text-align: left;

      &:hover {
        background: #eee;
        cursor: pointer;
      }
    }
  }
}

</style>




// WEBPACK FOOTER //
// src/components/general/MixedContentInput.vue